/**
 * Slide-in cart.
 *
 * - Product forms add to the cart without leaving the page, then the cart
 *   opens with the new line in it.
 * - Quantity and remove buttons update the cart in place.
 * - Suggested products under the lines are added with one press.
 * - The cart icon opens the drawer (on the cart page it stays a link).
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-cart-panel]');
      this.sectionId = 'cart-drawer';
      this.lastFocus = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) this.close();
        const change = event.target.closest('[data-cart-quantity]');
        if (change) this.change(change.dataset.lineKey, Number(change.dataset.cartQuantity));
        const extra = event.target.closest('[data-cart-add]');
        if (extra) this.addExtra(Number(extra.dataset.cartAdd));
      });

      this.addEventListener('change', (event) => {
        const note = event.target.closest('[data-cart-note]');
        if (note) this.saveNote(note.value);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.keepFocusInside(event);
      });

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        if (!opener || document.body.classList.contains('template-cart')) return;
        event.preventDefault();
        this.open(opener);
      });

      document.addEventListener('submit', (event) => {
        const form = event.target.closest('form[data-product-form]');
        if (!form) return;
        event.preventDefault();
        this.add(form, event.submitter);
      });
    }

    /* Tab and Shift+Tab go round inside the open cart instead of into the page behind it. */
    keepFocusInside(event) {
      const items = Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary'
        )
      ).filter((element) => element.getClientRects().length > 0);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = opener || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('has-open-drawer');
      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.classList.add('is-open');
        this.panel.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('has-open-drawer');
      window.setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, 200);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    async add(form, submitter) {
      const button = submitter || form.querySelector('[data-add-button]');
      const error = form.closest('.buy') ? form.closest('.buy').querySelector('[data-add-error]') : null;
      if (error) error.hidden = true;
      if (button) button.setAttribute('aria-busy', 'true');

      const body = new FormData(form);
      /* Fields attached with the form attribute (prescription details) sit outside the form element. */
      body.append('sections', this.sectionId);
      body.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${form.action}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || '');
        this.render(data.sections && data.sections[this.sectionId]);
        this.open(button);
      } catch (failure) {
        if (error) {
          error.textContent = failure.message || error.dataset.fallback || '';
          error.hidden = false;
        } else {
          form.submit();
        }
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }

    async change(key, quantity) {
      this.classList.add('is-busy');
      try {
        const response = await fetch(`${this.dataset.changeUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (response.ok) this.render(data.sections && data.sections[this.sectionId]);
      } finally {
        this.classList.remove('is-busy');
      }
    }

    saveNote(note) {
      fetch(`${this.dataset.updateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      }).catch(() => {
        /* The note can still be written on the cart page. */
      });
    }

    async addExtra(id) {
      this.classList.add('is-busy');
      try {
        const response = await fetch(`${this.dataset.addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id, quantity: 1 }],
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (response.ok) this.render(data.sections && data.sections[this.sectionId]);
      } finally {
        this.classList.remove('is-busy');
      }
    }

    render(markup) {
      if (!markup) return;
      const fresh = new DOMParser().parseFromString(markup, 'text/html');
      const note = this.querySelector('[data-cart-note]');
      const typed = note && document.activeElement === note ? note.value : null;
      const hadFocus = this.contains(document.activeElement);
      ['[data-cart-body]', '[data-cart-footer]'].forEach((selector) => {
        const current = this.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (current && next) current.replaceWith(next);
      });
      const freshNote = this.querySelector('[data-cart-note]');
      if (typed !== null && freshNote) {
        freshNote.value = typed;
        freshNote.focus();
      } else if (hadFocus && !this.contains(document.activeElement)) {
        /* The pressed button was replaced with the refreshed cart: keep the keyboard inside. */
        this.panel.focus();
      }
      const body = this.querySelector('[data-cart-body]');
      const count = body ? Number(body.dataset.itemCount) : 0;
      const express = this.querySelector('[data-cart-express]');
      if (express) express.hidden = count === 0;
      document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
        bubble.textContent = String(count);
        bubble.hidden = count === 0;
      });
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
